import {
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import isEmpty from "../../../common/helpers/isEmpty";
import { INVOICE_BREAKDOWN_OPTIONS } from "../../customer/contract/constants";
import { InvoiceBreakDownRows } from "../invoiceAPI";

type Props = {
  rows: InvoiceBreakDownRows;
};

export const ENTRY_BREAKDOWN_SECTION_HEADERS = [
  "WorktimeEntries",
  "OtherAbsences",
  "SickLeaves",
  "MidweekHolidays",
  "ShortTimeWorkingSchemes",
  "Holidays",
  "FlexLeaves",
  "WorkingTimeBankLeaves",
] as const;

const isInvoiceBreakDownOptionTitle = (row: string) =>
  INVOICE_BREAKDOWN_OPTIONS.includes(row);

const isEntryBreakdownSectionHeader = (row: string) => {
  const [section, ...columns] = row.split(";");
  return (
    ENTRY_BREAKDOWN_SECTION_HEADERS.some((header) => header === section) &&
    columns.join(";") === "Duration;WorkTitle;Talent"
  );
};

const PreviewInvoiceBreakDownRows = ({ rows }: Props) => {
  const { t } = useTranslation("invoices");

  if (!rows || isEmpty(rows)) return null;

  const getColumnsLength = () =>
    rows.find((row) => row.includes(";"))?.split(";").length ?? 1;

  const renderSumRow = (value: string, index: number) => {
    // description;value
    // e.g. Sum;3294.60
    const isDescriptionValue = index === 0;

    return (
      <>
        <TableCell
          // colSpan -> Sum;empty;value
          colSpan={isDescriptionValue ? 2 : 0}
          key={`invoiceBreakdownSumRow_${index}_${value}`}
        >
          {isDescriptionValue ? t("invoices:sum") : value}
        </TableCell>
      </>
    );
  };

  const renderCells = (row: string, header: boolean) => {
    const isSumRow = row.startsWith("Sum") && !header;

    return row
      .split(";")
      .map((value, index) =>
        isSumRow ? (
          renderSumRow(value, index)
        ) : (
          <TableCell key={`invoiceBreakdownRow_${index}_${value}`}>
            {header
              ? t(`invoiceBreakdownRows.header.${value}`) || value
              : value}
          </TableCell>
        ),
      );
  };

  return (
    <Grid
      container
      spacing={0}
      sx={{ borderTop: "1px solid black", marginBottom: "25px" }}
    >
      <Grid
        item
        xs={12}
        sx={{ paddingTop: "16px", borderBottom: "1px solid black" }}
      >
        <Typography>
          {t("invoiceBreakdownRows.invoiceBreakdownTitle")}
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Table size="small">
          {rows.map((row, index) => {
            const isTitleRow = isInvoiceBreakDownOptionTitle(row);
            const isSectionHeader = isEntryBreakdownSectionHeader(row);
            const isHeaderAfterTitle =
              index > 0 &&
              isInvoiceBreakDownOptionTitle(rows[index - 1]) &&
              !isSectionHeader;

            if (isTitleRow) {
              const nextRow = rows[index + 1];
              const hasTraditionalHeader =
                nextRow && !isEntryBreakdownSectionHeader(nextRow);
              return (
                <TableHead key={index}>
                  <TableRow key={index}>
                    <TableCell
                      sx={{ fontWeight: 600, paddingLeft: 0 }}
                      colSpan={getColumnsLength()}
                    >
                      {t(`invoiceBreakdownRows.${row}`) || row}
                    </TableCell>
                  </TableRow>
                  {hasTraditionalHeader && (
                    <TableRow sx={{ "& > th": { fontWeight: 600 } }}>
                      {renderCells(nextRow, true)}
                    </TableRow>
                  )}
                </TableHead>
              );
            }

            if (isSectionHeader) {
              return (
                <TableHead key={index}>
                  <TableRow sx={{ "& > th": { fontWeight: 600 } }}>
                    {renderCells(row, true)}
                  </TableRow>
                </TableHead>
              );
            }

            if (!isHeaderAfterTitle) {
              return (
                <TableBody key={index}>
                  <TableRow>{renderCells(row, false)}</TableRow>
                </TableBody>
              );
            }

            return null;
          })}
        </Table>
      </Grid>
    </Grid>
  );
};

export default PreviewInvoiceBreakDownRows;
